import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { of, switchMap } from 'rxjs';
import { DatePickerRange } from '../date-picker/date-picker.component';
import { DeliveryPlanningComponent } from './delivery-planning/delivery-planning.component';
import { DrillPlanningComponent } from './drill-planning/drill-planning.component';
import { PlanningActivityService } from './planning-activity.service';
import { DrillColorBy, PeriodBounds, PlanningMode, TimeScale } from './planning.types';
import { PlanningControlsComponent } from './ui/planning-controls/planning-controls.component';
import { computeBounds, fromIsoDateString, shiftAnchor, toIsoDateString } from './utils/column-generator';

/**
 * Onglet Planning : détient la barre de contrôles commune aux sous-onglets
 * forage et livraison, pour un site. Partagé par les écrans area et site.
 */
@Component({
  selector: 'app-planning',
  standalone: true,
  templateUrl: './planning.component.html',
  styleUrls: ['./planning.component.scss'],
  imports: [TranslateModule, PlanningControlsComponent, DrillPlanningComponent, DeliveryPlanningComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningComponent {
  /** Site courant ; l'arbre le fournit sous forme de chaîne ou de nombre. */
  readonly siteId = input<string | number | null>(null);

  // Vue affichée. Liables en `[(…)]` : l'écran site les porte dans l'URL, pour que le
  // retour arrière depuis un tir ouvert ici retrouve la même période.
  /** Sous-onglet actif. */
  readonly mode      = model<PlanningMode>('drilling');
  readonly colorBy   = model<DrillColorBy>('rig');
  readonly timeScale = model<TimeScale>('week');
  /** Jour de référence de la période (`YYYY-MM-DD`, jour local) ; null = aujourd'hui. */
  readonly date      = model<string | null>(null);

  private readonly anchorDate = computed<Date>(() => fromIsoDateString(this.date()) ?? new Date());

  protected readonly deliveryPlanning = viewChild(DeliveryPlanningComponent);

  /** Le rapport ne porte que sur des livraisons : il faut au moins un shift sélectionné qui en contienne. */
  protected readonly reportDisabled = computed<boolean>(() =>
    this.mode() !== 'delivery' || !this.deliveryPlanning()?.canReport(),
  );

  /** Identifiant exploitable par les services, ou null si rien n'est sélectionné. */
  protected readonly resolvedSiteId = computed<number | null>(() => {
    const raw = this.siteId();
    if (raw === null || raw === '') { return null; }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });

  private readonly activity = inject(PlanningActivityService);

  /** Période affichée par le sélecteur de date ; null tant qu'il n'a pas été ouvert. */
  protected readonly pickerRange = signal<DatePickerRange | null>(null);

  /** Points du sélecteur : activité du sous-onglet courant sur la période affichée. */
  protected readonly markedDates = toSignal(
    toObservable(computed(() => ({ siteId: this.resolvedSiteId(), mode: this.mode(), range: this.pickerRange() }))).pipe(
      switchMap(({ siteId, mode, range }) =>
        siteId !== null && range ? this.activity.getActivePeriods(siteId, mode, range) : of([] as string[]),
      ),
    ),
    { initialValue: [] as string[] },
  );

  protected readonly bounds = computed<PeriodBounds>(() =>
    computeBounds(this.timeScale(), this.anchorDate()),
  );

  protected readonly anchorIsoDate = computed<string>(() => toIsoDateString(this.anchorDate()));

  /** Le premier critère de couleur diffère selon le sous-onglet. */
  protected readonly primaryColorByLabel = computed<string>(() =>
    this.mode() === 'drilling' ? 'PLANNING.RIG' : 'PLANNING.EQUIPMENT',
  );

  protected setMode(mode: PlanningMode): void {
    this.mode.set(mode);
  }

  protected onColorByChange(value: DrillColorBy): void {
    this.colorBy.set(value);
  }

  protected onTimeScaleChange(scale: TimeScale): void {
    this.timeScale.set(scale);
  }

  protected onToday(): void {
    this.date.set(null);
  }

  protected onNavigate(direction: -1 | 1): void {
    this.setAnchor(shiftAnchor(this.timeScale(), this.anchorDate(), direction));
  }

  private setAnchor(anchor: Date): void {
    this.date.set(toIsoDateString(anchor));
  }
}
